import { isDurableMeasuredRevision } from './buildPipelines.js';
import { PlatformError } from '@djgg/core';
import { canonicalJson } from '@djgg/gameplay-kit';
import type { BuildPipelineSnapshot } from './buildPipelines.js';
import type { BuildJob, Creation, CreationVersion, FailedBuildCheckpoint } from './types.js';

/** A checkpoint is repair input, never evidence that a version is playable. */
export function requireFailedBuildCheckpoint(
  creationId: string,
  creation: Creation,
  failed: CreationVersion | null,
  prompt: string,
  pipeline?: BuildPipelineSnapshot,
  measuredCheckpoint?: FailedBuildCheckpoint,
): FailedBuildCheckpoint {
  const checkpoint = measuredCheckpoint ?? failed?.gates.failedCheckpoint;
  const prior = failed?.pipelineSnapshot;
  const lineage = failed?.gates.acceptedLineage;
  const resumablePipeline =
    (prior?.config?.construction === 'core-first' && prior.stage === 'prototype') ||
    (prior?.config?.construction === 'verified-core' && prior.stage === 'standard') ||
    (prior?.config?.construction === 'measured-first-playable' && prior.stage === 'standard');
  const sameConfig = !pipeline || (prior?.config && pipeline.config &&
    canonicalJson(prior.config) === canonicalJson(pipeline.config));
  if (!failed || failed.n !== creation.latestVersion || failed.buildStatus !== 'failed' || !checkpoint ||
      !resumablePipeline ||
      (failed.designRev ?? 0) !== (creation.designRev ?? 0) ||
      (failed.settingsRev ?? 0) !== (creation.settingsRev ?? 0) ||
      !lineage || lineage.baseVersion !== (creation.activeVersion ?? 0) || failed.prompt !== prompt ||
      (pipeline && (pipeline.id !== prior.id || pipeline.revision !== prior.revision || pipeline.stage !== prior.stage || !sameConfig)) ||
      !(measuredCheckpoint
        ? checkpoint.sourcePath.includes(`/build-candidates/${creationId}/v${failed.n}/`)
        : /^gs:\/\/[^/]+\/build-checkpoints\//.test(checkpoint.sourcePath)
          && checkpoint.sourcePath.includes(`/build-checkpoints/${creationId}/v${failed.n}/`)) ||
      !/^[a-f0-9]{64}$/.test(checkpoint.sourceDigest) ||
      !['core', 'complete'].includes(checkpoint.constructionStage) || !checkpoint.failureText) {
    throw new PlatformError('conflict', 'saved build is stale or unavailable; start a new build for the current design, settings, source and pipeline');
  }
  return checkpoint;
}

export function sameFailedCheckpoint(a: FailedBuildCheckpoint, b: FailedBuildCheckpoint): boolean {
  return a.sourcePath === b.sourcePath && a.sourceDigest === b.sourceDigest &&
    a.constructionStage === b.constructionStage && a.failureText === b.failureText;
}

/** Recover only the exact source selected by the failed measured job. Neither
 * a runnable bundle nor passed gates are required to continue editing it. */
export function measuredFailedBuildCheckpoint(
  creationId: string,
  version: number,
  job: BuildJob,
  expectedRecipeRevision?: number,
): FailedBuildCheckpoint | null {
  const recipeRevision = Number(job.measuredExecution?.recipe.revision);
  if (job.creationId !== creationId || job.version !== version || job.status !== 'failed'
    || job.measuredExecution?.recipe.id !== 'measured-first-playable'
    || !isDurableMeasuredRevision(recipeRevision)
    || expectedRecipeRevision !== undefined && recipeRevision !== expectedRecipeRevision) {
    throw new PlatformError('conflict', 'saved measured build is not the failed version');
  }
  const checkpoint = job.measuredExecution?.checkpoint;
  if (!checkpoint) return null; // Initial authoring never retained source.
  const candidate = checkpoint.candidates[checkpoint.currentCandidateId];
  const match = candidate?.source.path.match(/^gs:\/\/[^/]+\/build-candidates\/([^/]+)\/v(\d+)\/attempt-\d+\/([a-f0-9]{64})\/source\.tgz$/);
  if (!candidate || candidate.candidateId !== checkpoint.currentCandidateId
    || !match || match[1] !== creationId || Number(match[2]) !== version || match[3] !== candidate.candidateId
    || !/^[a-f0-9]{64}$/.test(candidate.source.sha256) || !candidate.source.generation
    || !Number.isSafeInteger(checkpoint.revision) || checkpoint.revision < 1
    || checkpoint.executionState.checkpointRevision !== checkpoint.revision
    || checkpoint.executionState.currentCandidateId !== checkpoint.currentCandidateId
    || (checkpoint.executionState.bestCandidateId ?? null) !== checkpoint.bestCandidateId
    || (checkpoint.bestCandidateId !== null
      && checkpoint.candidates[checkpoint.bestCandidateId]?.candidateId !== checkpoint.bestCandidateId)) {
    throw new PlatformError('conflict', 'retained measured source authority is invalid');
  }
  // Older early-stop reports hid observed failures behind coverage text. Carry
  // that existing evidence into repair feedback without manufacturing a pass.
  const report = candidate.report as { failureText?: string; candidateAudit?: {
    tier1?: { criteria?: Record<string, { pass?: boolean; evidence?: string }> };
  } };
  const observed = Object.entries(report.candidateAudit?.tier1?.criteria ?? {})
    .filter(([, criterion]) => criterion.pass === false && typeof criterion.evidence === 'string')
    .map(([name, criterion]) => `${name}: ${criterion.evidence}`).join('\n');
  const failureText = [observed, report.failureText || 'Retained source requires full validation before release.'].filter(Boolean).join('\n').slice(0, 16000);
  return { sourcePath: candidate.source.path, sourceDigest: candidate.source.sha256,
    constructionStage: 'complete', failureText };
}
